<template>
  <section class="cob">
    <!-- Encabezado: período, moneda y descarga -->
    <div class="cob-header">
      <h1>Cobranzas</h1>
      <div class="cob-acciones">
        <input v-model="filtros.from" type="date" class="cob-input" @change="recargar" />
        <span class="cob-sep">a</span>
        <input v-model="filtros.to" type="date" class="cob-input" @change="recargar" />

        <div class="cob-moneda">
          <button v-for="m in monedas" :key="m.key" class="cob-moneda-op"
            :class="{ 'is-activa': filtros.moneda === m.key }" @click="cambiarMoneda(m.key)">
            {{ m.label }}
          </button>
        </div>

        <button class="cob-btn cob-btn--primary" :disabled="descargando" @click="descargar">
          <i class="ri-download-2-line"></i>
          {{ descargando ? 'Generando…' : 'Descargar Excel' }}
        </button>
      </div>
    </div>

    <!-- Qué tipo de cambio se usó. Un total convertido sin decir a cuánto es
         un número que nadie puede auditar (§7 del DEF). -->
    <p v-if="totales && totales.tc_usado" class="cob-tc">
      <i class="ri-information-line"></i>
      Convertido con el tipo de cambio de la fecha de cada viaje:
      <b v-if="totales.tc_usado.unico">{{ num(totales.tc_usado.minimo) }}</b>
      <b v-else>entre {{ num(totales.tc_usado.minimo) }} y {{ num(totales.tc_usado.maximo) }}</b>
      <span v-if="totales.tc_arrastrados">
        · {{ totales.tc_arrastrados }}
        {{ totales.tc_arrastrados === 1 ? 'viaje usó' : 'viajes usaron' }}
        el tipo de cambio de un día anterior (fin de semana o feriado)
      </span>
      <span v-if="totales.sin_convertir" class="cob-tc-falta">
        · {{ totales.sin_convertir }} sin convertir por falta de tipo de cambio
      </span>
    </p>

    <!-- Franja de revisión -->
    <div v-if="totales && totales.alertas_total" class="cob-franja">
      <i class="ri-error-warning-line"></i>
      <span>
        <b>{{ totales.alertas_total }}</b>
        {{ totales.alertas_total === 1 ? 'viaje necesita' : 'viajes necesitan' }} atención:
        <template v-if="totales.alerta_sin_respaldo">
          {{ totales.alerta_sin_respaldo }} cobrado sin respaldo
        </template>
        <template v-if="totales.alerta_costo_mayor">
          · {{ totales.alerta_costo_mayor }} cuesta más de lo que se vendió
        </template>
        <template v-if="totales.alerta_liq_sin_numero">
          · {{ totales.alerta_liq_sin_numero }} liquidado sin número
        </template>
      </span>
      <button class="cob-franja-btn" :class="{ 'is-activo': filtros.alerta === 'TODAS' }"
        @click="alternarFiltro('alerta', 'TODAS')">
        {{ filtros.alerta === 'TODAS' ? 'Ver todos' : 'Ver solo estos' }}
      </button>
    </div>

    <!-- Las cifras -->
    <div v-if="totales" class="cob-bloques">
      <div class="cob-bloque">
        <h2>Lo que entra</h2>
        <div class="cob-tarjetas">
          <div class="cob-card cob-card--total">
            <span class="cob-card-label">Facturado</span>
            <span class="cob-card-valor">{{ money(totales.facturado) }}</span>
            <span class="cob-card-pie">{{ totales.viajes }} viajes</span>
          </div>
          <button v-for="c in tarjetasEntra" :key="c.estado" class="cob-card cob-card--click"
            :class="[`cob-card--${c.clase}`, { 'is-activa': filtros.estado_pago === c.estado }]"
            @click="alternarFiltro('estado_pago', c.estado)">
            <span class="cob-card-label">{{ c.label }}</span>
            <span class="cob-card-valor">{{ money(totales[c.campo]) }}</span>
            <span class="cob-card-pie">{{ c.pie }}</span>
          </button>
        </div>
      </div>

      <div class="cob-bloque">
        <h2>Lo que sale</h2>
        <div class="cob-tarjetas">
          <button v-for="c in tarjetasSale" :key="c.tipo" class="cob-card cob-card--click"
            :class="{ 'is-activa': filtros.tipo_proveedor === c.tipo }"
            @click="alternarFiltro('tipo_proveedor', c.tipo)">
            <span class="cob-card-label">{{ c.label }}</span>
            <span class="cob-card-valor">{{ money(totales[c.campo]) }}</span>
          </button>
          <div class="cob-card cob-card--margen">
            <span class="cob-card-label">Margen</span>
            <span class="cob-card-valor">{{ money(totales.margen) }}</span>
            <span class="cob-card-pie">costo total {{ money(totales.costo_total) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- La línea fija: sin ella el margen sale inflado y nadie sabe por qué -->
    <p v-if="totales" class="cob-nota">
      <button class="cob-nota-link" :class="{ 'is-activo': filtros.sin_costo }"
        @click="filtros.sin_costo = !filtros.sin_costo; recargar()">
        {{ totales.viajes_sin_costo }}
        {{ totales.viajes_sin_costo === 1 ? 'viaje no tiene' : 'viajes no tienen' }}
        costo cargado
      </button>
      y quedan fuera del costo y del margen.
      <span v-if="totales.viajes_con_extras">
        · El costo incluye {{ money(totales.extras_total) }} de extras en
        {{ totales.viajes_con_extras }}
        {{ totales.viajes_con_extras === 1 ? 'viaje' : 'viajes' }}.
      </span>
      <span v-if="excluidos" class="cob-nota-gris">
        · No se muestran {{ excluidos }} viajes cancelados.
      </span>
    </p>

    <!-- Filtros -->
    <div class="cob-filtros">
      <select v-model="filtros.city_id" class="cob-input" @change="recargar">
        <option :value="null">Todas las ciudades</option>
        <option v-for="c in ciudades" :key="c.id" :value="c.id">{{ c.name }}</option>
      </select>

      <select v-model="filtros.estado_pago" class="cob-input" @change="recargar">
        <option :value="null">Estado de pago</option>
        <option v-for="e in estadosPago" :key="e.key" :value="e.key">{{ e.label }}</option>
      </select>

      <select v-model="filtros.medio_pago" class="cob-input" @change="recargar">
        <option :value="null">Medio de pago</option>
        <option v-for="m in mediosPago" :key="m.key" :value="m.key">{{ m.label }}</option>
      </select>

      <select v-model="filtros.tipo_proveedor" class="cob-input" @change="recargar">
        <option :value="null">Tipo de proveedor</option>
        <option value="PROPIO">Flota propia</option>
        <option value="ASOCIADO">Asociado</option>
        <option value="TRANSPORTISTA">Transportista</option>
      </select>

      <input v-model="filtros.fuente" type="text" class="cob-input" placeholder="Fuente"
        @keyup.enter="recargar" @blur="recargar" />

      <input v-model="filtros.ota_settlement_code" type="text" class="cob-input"
        placeholder="N° de liquidación" @keyup.enter="recargar" @blur="recargar" />

      <input v-model="filtros.search" type="text" class="cob-input cob-input--ancho"
        placeholder="Código o pasajero" @keyup.enter="recargar" @blur="recargar" />

      <button v-if="hayFiltros" class="cob-btn cob-btn--limpiar" @click="limpiar">
        <i class="ri-close-line"></i> Limpiar
      </button>

      <div class="cob-columnas">
        <button class="cob-btn" @click="menuColumnas = !menuColumnas">
          <i class="ri-layout-column-line"></i> Columnas
        </button>
        <div v-if="menuColumnas" class="cob-columnas-menu">
          <label v-for="c in columnasOpcionales" :key="c.key" class="cob-columnas-op">
            <input type="checkbox" :checked="visibles.includes(c.key)" @change="alternarColumna(c.key)" />
            {{ c.label }}
          </label>
        </div>
      </div>
    </div>

    <!-- La tabla -->
    <div class="cob-tabla-wrap">
      <table class="cob-tabla">
        <thead>
          <tr>
            <th v-for="c in columnasVisibles" :key="c.key" :class="c.clase">{{ c.label }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="f in filas" :key="f.id" :class="{ 'cob-fila--alerta': tieneAlerta(f) }">
            <td v-for="c in columnasVisibles" :key="c.key" :class="c.clase">
              <!-- marca -->
              <template v-if="c.key === 'marca'">
                <span v-if="tieneAlerta(f)" class="cob-marca" :title="motivos(f)">⚠</span>
              </template>

              <template v-else-if="c.key === 'proveedor'">
                <template v-if="f.proveedor">
                  {{ f.proveedor }}
                  <span v-if="f.proveedor_tipo" class="cob-sub">{{ f.proveedor_tipo }}</span>
                </template>
                <span v-else class="cob-vacio">—</span>
              </template>

              <template v-else-if="c.key === 'precio'">
                {{ montoDe(f, 'precio') }}
              </template>

              <template v-else-if="c.key === 'costo'">
                <span v-if="f.sin_costo" class="cob-vacio">sin costo</span>
                <template v-else>
                  {{ montoDe(f, 'costo') }}
                  <span v-if="f.costo_extras" class="cob-sub">
                    incluye {{ num(f.costo_extras) }} de extras
                  </span>
                </template>
              </template>

              <template v-else-if="c.key === 'margen'">
                <span v-if="f.sin_costo" class="cob-vacio">—</span>
                <span v-else :class="{ 'cob-negativo': esNegativo(f) }">
                  {{ montoDe(f, 'margen') }}
                </span>
              </template>

              <template v-else-if="c.key === 'respaldo'">
                <span v-if="respaldoFalta(f)" class="cob-falta">{{ respaldoTexto(f) }}</span>
                <span v-else-if="respaldoTexto(f)">{{ respaldoTexto(f) }}</span>
                <span v-else class="cob-vacio">—</span>
              </template>

              <template v-else-if="c.key === 'liquidacion'">
                <span v-if="f.alerta_liq_sin_numero" class="cob-falta">falta</span>
                <span v-else>{{ f.ota_settlement_code || '—' }}</span>
              </template>

              <template v-else>{{ celda(f, c.key) }}</template>
            </td>
          </tr>

          <tr v-if="!filas.length && !cargando">
            <td :colspan="columnasVisibles.length" class="cob-empty">
              No hay viajes en este período con los filtros aplicados.
            </td>
          </tr>
        </tbody>

        <!-- El pie trae los totales, y repite que el margen excluye los
             viajes sin costo (§5.4). -->
        <tfoot v-if="totales && filas.length">
          <tr>
            <td v-for="c in columnasVisibles" :key="c.key" :class="c.clase">
              <template v-if="c.key === 'codigo'">
                Total del período ({{ totales.viajes }} viajes)
              </template>
              <template v-else-if="c.key === 'precio'">{{ money(totales.facturado) }}</template>
              <template v-else-if="c.key === 'costo'">{{ money(totales.costo_total) }}</template>
              <template v-else-if="c.key === 'margen'">
                {{ money(totales.margen) }}
                <span class="cob-sub">sin {{ totales.viajes_sin_costo }} sin costo</span>
              </template>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>

    <!-- Subtotales del modo "sin convertir": las monedas no se suman -->
    <div v-if="totales && totales.subtotales && totales.subtotales.length" class="cob-subtotales">
      <h3>Subtotales por moneda</h3>
      <p class="cob-subtotales-nota">
        Las monedas no se suman entre sí. Para un total único, elegí PEN o USD.
      </p>
      <table class="cob-tabla">
        <thead>
          <tr>
            <th>Moneda</th><th class="num">Viajes</th><th class="num">Facturado</th>
            <th class="num">Cobrado</th><th class="num">Liquidado</th>
            <th class="num">Pendiente</th><th class="num">Costo</th><th class="num">Margen</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="s in totales.subtotales" :key="s.moneda">
            <td>{{ s.moneda }}</td>
            <td class="num">{{ s.viajes }}</td>
            <td class="num">{{ num(s.facturado) }}</td>
            <td class="num">{{ num(s.cobrado) }}</td>
            <td class="num">{{ num(s.liquidado) }}</td>
            <td class="num">{{ num(s.pendiente) }}</td>
            <td class="num">{{ num(s.costo_total) }}</td>
            <td class="num">{{ num(s.margen) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="totalPaginas > 1" class="cob-paginador">
      <button class="cob-btn" :disabled="pagina === 0" @click="irA(pagina - 1)">Anterior</button>
      <span>Página {{ pagina + 1 }} de {{ totalPaginas }} · {{ totalFilas }} viajes</span>
      <button class="cob-btn" :disabled="pagina + 1 >= totalPaginas" @click="irA(pagina + 1)">
        Siguiente
      </button>
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex'
import { Alert } from 'wize-admin'
import { CollectionsService } from '@/core/services'
import { Constants as LocalConstants } from '@/core/utils'
import { hoyLocal } from '@/core/utils/fecha'

// Las quince columnas del §5.4, en orden. 'marca' no se puede ocultar: es la
// que avisa que la fila tiene un problema.
const COLUMNAS = [
  { key: 'marca', label: '', fijo: true, clase: 'marca' },
  { key: 'codigo', label: 'Código' },
  { key: 'fecha', label: 'Fecha' },
  { key: 'ciudad', label: 'Ciudad' },
  { key: 'fuente', label: 'Fuente' },
  { key: 'pasajero', label: 'Pasajero' },
  { key: 'proveedor', label: 'Proveedor' },
  { key: 'precio', label: 'Precio', clase: 'num' },
  { key: 'costo', label: 'Costo', clase: 'num' },
  { key: 'margen', label: 'Margen', clase: 'num' },
  { key: 'estado', label: 'Estado' },
  { key: 'medio', label: 'Medio' },
  { key: 'liquidacion', label: 'N° liquidación' },
  { key: 'respaldo', label: 'Respaldo' },
  { key: 'fechaPago', label: 'Fecha pago' },
]

const CLAVE_COLUMNAS = 'cobranzas.columnas'

// Primer día del mes en curso, en hora local. No se usa toISOString por lo
// mismo que explica fecha.js: después de las 19:00 de Lima devuelve otro día.
function primerDiaDelMes() {
  const d = new Date()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  return `${d.getFullYear()}-${mm}-01`
}

function filtrosVacios() {
  return {
    from: primerDiaDelMes(),
    to: hoyLocal(),
    moneda: 'PEN',
    city_id: null,
    estado_pago: null,
    medio_pago: null,
    tipo_proveedor: null,
    fuente: '',
    ota_settlement_code: '',
    search: '',
    alerta: null,
    sin_costo: false,
  }
}

export default {
  name: 'CollectionsList',
  data() {
    return {
      filtros: filtrosVacios(),
      totales: null,
      filas: [],
      pagina: 0,
      tamano: 50,
      totalFilas: 0,
      totalPaginas: 0,
      cargando: false,
      descargando: false,
      menuColumnas: false,
      visibles: COLUMNAS.map((c) => c.key),
      monedas: [
        { key: 'PEN', label: 'PEN' },
        { key: 'USD', label: 'USD' },
        { key: '', label: 'Sin convertir' },
      ],
      tarjetasEntra: [
        { estado: 'Pagado', campo: 'cobrado', label: 'Cobrado', clase: 'cobrado', pie: 'ya entró' },
        { estado: 'Liquidado', campo: 'liquidado', label: 'Liquidado', clase: 'liquidado', pie: 'comprometido' },
        { estado: 'Pendiente', campo: 'pendiente', label: 'Pendiente', clase: 'pendiente', pie: 'sin comprometer' },
      ],
      tarjetasSale: [
        { tipo: 'PROPIO', campo: 'costo_propio', label: 'Flota propia' },
        { tipo: 'ASOCIADO', campo: 'costo_asociado', label: 'Asociados' },
        { tipo: 'TRANSPORTISTA', campo: 'costo_transportista', label: 'Transportistas' },
      ],
    }
  },
  computed: {
    ...mapGetters({ ciudades: 'city/getCities' }),
    estadosPago() {
      return Object.values(LocalConstants.RideEstadoPago)
    },
    mediosPago() {
      return Object.values(LocalConstants.RideMedioPago)
    },
    columnasOpcionales() {
      return COLUMNAS.filter((c) => !c.fijo)
    },
    columnasVisibles() {
      return COLUMNAS.filter((c) => c.fijo || this.visibles.includes(c.key))
    },
    // El símbolo depende del modo. En "sin convertir" cada fila trae el suyo,
    // así que acá queda vacío y lo pone montoDe.
    simbolo() {
      if (this.filtros.moneda === 'PEN') return 'S/'
      if (this.filtros.moneda === 'USD') return 'US$'
      return ''
    },
    // Lo calcula el backend y no se suman los dos conteos: un viaje puede
    // estar cancelado de las dos formas a la vez y se contaría dos veces.
    excluidos() {
      return this.totales ? this.totales.viajes_excluidos : 0
    },
    hayFiltros() {
      const base = filtrosVacios()
      return ['city_id', 'estado_pago', 'medio_pago', 'tipo_proveedor', 'fuente',
        'ota_settlement_code', 'search', 'alerta', 'sin_costo']
        .some((k) => this.filtros[k] !== base[k])
    },
  },
  mounted() {
    this.restaurarColumnas()
    this.$store.dispatch('city/loadCities')
    this.cargar()
  },
  methods: {
    // --- formato ---
    num(v) {
      if (v === null || v === undefined || v === '') return '—'
      return Number(v).toLocaleString('es-PE', {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      })
    },
    money(v) {
      if (v === null || v === undefined) return '—'
      return this.simbolo ? `${this.simbolo} ${this.num(v)}` : this.num(v)
    },
    // montoDe elige entre el valor convertido y el original según el modo, y
    // marca la fila que no se pudo convertir en vez de mostrar un cero.
    montoDe(f, campo) {
      if (this.filtros.moneda === '') {
        const simbolo = f.precio_moneda === 'USD' ? 'US$' : 'S/'
        const original = campo === 'margen' ? null : f[campo]
        return original === null || original === undefined
          ? '—' : `${simbolo} ${this.num(original)}`
      }
      const v = f[`${campo}_convertido`]
      if (v === null || v === undefined) {
        const simbolo = f.precio_moneda === 'USD' ? 'US$' : 'S/'
        return campo === 'precio' ? `${simbolo} ${this.num(f.precio)} (sin convertir)` : '—'
      }
      return this.money(v)
    },
    celda(f, key) {
      const mapa = {
        codigo: f.codigo,
        fecha: f.fecha,
        ciudad: f.ciudad,
        fuente: f.fuente,
        pasajero: f.pasajero,
        estado: f.estado_pago,
        medio: f.medio_pago,
        fechaPago: f.fecha_pago,
      }
      return mapa[key] || '—'
    },

    // --- alertas ---
    // Las tres condiciones las decide el backend; acá solo se pintan. Si la
    // regla viviera también en el frontend, serían dos definiciones.
    tieneAlerta(f) {
      return f.alerta_sin_respaldo || f.alerta_costo_mayor || f.alerta_liq_sin_numero
    },
    motivos(f) {
      const m = []
      if (f.alerta_sin_respaldo) m.push('Cobrado sin comprobante ni N° de operación')
      if (f.alerta_costo_mayor) m.push('Cuesta más de lo que se vendió')
      if (f.alerta_liq_sin_numero) m.push('Liquidado sin número')
      return m.join(' · ')
    },
    esNegativo(f) {
      const v = f.margen_convertido
      return v !== null && v !== undefined && Number(v) < 0
    },
    respaldoFalta(f) {
      return f.alerta_sin_respaldo || f.alerta_liq_sin_numero
    },
    // Lo que respalda un cobro no es lo mismo según el estado: en un viaje de
    // OTA es el número de liquidación, en uno directo el comprobante o la
    // operación (§5.4).
    respaldoTexto(f) {
      if (f.estado_pago === 'Liquidado') {
        return f.alerta_liq_sin_numero
          ? 'Falta N° de liquidación' : (f.ota_settlement_code || '')
      }
      if (f.estado_pago === 'Pagado') {
        if (f.alerta_sin_respaldo) return 'Falta comprobante y N° de operación'
        return [f.comprobante, f.num_operacion].filter(Boolean).join(' · ')
      }
      return ''
    },

    // --- columnas ---
    alternarColumna(key) {
      const i = this.visibles.indexOf(key)
      if (i >= 0) this.visibles.splice(i, 1)
      else this.visibles.push(key)
      try {
        localStorage.setItem(CLAVE_COLUMNAS, JSON.stringify(this.visibles))
      } catch (e) {
        // Si el navegador no deja guardar, la pantalla sigue funcionando:
        // solo no recuerda la selección la próxima vez.
      }
    },
    restaurarColumnas() {
      try {
        const raw = localStorage.getItem(CLAVE_COLUMNAS)
        if (raw) {
          const guardadas = JSON.parse(raw)
          if (Array.isArray(guardadas) && guardadas.length) this.visibles = guardadas
        }
      } catch (e) {
        // Dato corrupto: se queda con todas las columnas.
      }
    },

    // --- filtros ---
    alternarFiltro(campo, valor) {
      this.filtros[campo] = this.filtros[campo] === valor ? null : valor
      this.recargar()
    },
    cambiarMoneda(m) {
      this.filtros.moneda = m
      this.recargar()
    },
    limpiar() {
      const { from, to, moneda } = this.filtros
      this.filtros = { ...filtrosVacios(), from, to, moneda }
      this.recargar()
    },
    recargar() {
      this.pagina = 0
      this.cargar()
    },
    irA(p) {
      this.pagina = p
      this.cargar()
    },

    // mensajeDeError saca el texto del backend de lo que rechaza el
    // interceptor, que no es un error de axios sino el cuerpo de la respuesta
    // —un objeto {status, errors:{message}}— o directamente una cadena.
    mensajeDeError(e, porDefecto) {
      if (typeof e === 'string') return e
      if (e && e.errors && e.errors.message) return e.errors.message
      if (e && e.message) return e.message
      return porDefecto
    },

    // params arma la query. Se sacan los vacíos para que la URL quede legible
    // y para no mandar filtros que el backend tendría que ignorar igual.
    params(conPagina = true) {
      const p = { from: this.filtros.from, to: this.filtros.to, moneda: this.filtros.moneda }
      const opcionales = ['city_id', 'estado_pago', 'medio_pago', 'tipo_proveedor',
        'fuente', 'ota_settlement_code', 'search', 'alerta']
      opcionales.forEach((k) => {
        const v = this.filtros[k]
        if (v !== null && v !== undefined && v !== '') p[k] = v
      })
      if (this.filtros.sin_costo) p.sin_costo = true
      if (conPagina) {
        p.page = this.pagina
        p.size = this.tamano
      }
      return p
    },

    async cargar() {
      this.cargando = true
      try {
        // Las dos en paralelo: los totales no dependen de la página y la
        // página no depende de los totales.
        const [totales, lista] = await Promise.all([
          CollectionsService.totals(this.params(false)),
          CollectionsService.list(this.params()),
        ])
        // El interceptor de http.js ya devuelve response.data para las
        // respuestas JSON, así que acá NO va otro .data. Solo las descargas
        // devuelven la respuesta entera, y por eso el Excel sí usa .data.
        this.totales = totales.payload
        const pagina = lista.payload
        this.filas = pagina.content || []
        this.totalFilas = pagina.total_elements || 0
        this.totalPaginas = pagina.total_pages || 0
      } catch (e) {
        Alert.error(this.mensajeDeError(e, 'No se pudo cargar el reporte de cobranzas'))
      } finally {
        this.cargando = false
      }
    },

    async descargar() {
      this.descargando = true
      try {
        const res = await CollectionsService.export(this.params(false))
        const blob = new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `Cobranzas_${this.filtros.from}_a_${this.filtros.to}.xlsx`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        Alert.success('Excel descargado')
      } catch (e) {
        Alert.error(this.mensajeDeError(e, 'No se pudo generar el Excel'))
      } finally {
        this.descargando = false
      }
    },
  },
}
</script>

<style lang="stylus" scoped>
.cob
  padding 16px 24px 40px

.cob-header
  display flex
  align-items center
  justify-content space-between
  flex-wrap wrap
  gap 12px

  h1
    font-size 22px
    font-weight 600
    margin 0

.cob-acciones
  display flex
  align-items center
  gap 8px
  flex-wrap wrap

.cob-sep
  color #6b7280
  font-size 13px

.cob-input
  border 1px solid #d1d5db
  border-radius 6px
  padding 6px 10px
  font-size 13px
  background #fff
  min-width 140px

.cob-input--ancho
  min-width 220px

.cob-moneda
  display inline-flex
  border 1px solid #d1d5db
  border-radius 6px
  overflow hidden

.cob-moneda-op
  border 0
  background #fff
  padding 6px 12px
  font-size 13px
  cursor pointer
  color #374151

  &.is-activa
    background #1a1f2e
    color #fff

.cob-btn
  border 1px solid #d1d5db
  background #fff
  border-radius 6px
  padding 6px 12px
  font-size 13px
  cursor pointer
  color #374151

  &:disabled
    opacity .6
    cursor default

.cob-btn--primary
  background #1a1f2e
  border-color #1a1f2e
  color #fff

.cob-btn--limpiar
  color #b91c1c
  border-color #fca5a5

.cob-tc
  margin 10px 0 0
  font-size 12px
  color #4b5563

.cob-tc-falta
  color #b45309
  font-weight 600

.cob-franja
  margin-top 12px
  display flex
  align-items center
  gap 10px
  background #fef3c7
  border 1px solid #fcd34d
  border-radius 8px
  padding 10px 14px
  font-size 13px
  color #78350f

  i
    font-size 18px

.cob-franja-btn
  margin-left auto
  border 1px solid #d97706
  background #fff
  color #92400e
  border-radius 6px
  padding 4px 10px
  font-size 12px
  cursor pointer

  &.is-activo
    background #92400e
    color #fff

.cob-bloques
  display grid
  grid-template-columns 1fr 1fr
  gap 16px
  margin-top 16px

  h2
    font-size 12px
    text-transform uppercase
    letter-spacing .5px
    color #6b7280
    margin 0 0 8px

.cob-tarjetas
  display grid
  grid-template-columns repeat(4, 1fr)
  gap 8px

.cob-card
  display flex
  flex-direction column
  gap 2px
  border 1px solid #e5e7eb
  border-radius 8px
  padding 10px 12px
  background #fff
  text-align left

.cob-card--click
  cursor pointer

  &:hover
    border-color #9ca3af

  &.is-activa
    border-color #1a1f2e
    box-shadow 0 0 0 1px #1a1f2e

.cob-card-label
  font-size 11px
  text-transform uppercase
  color #6b7280

.cob-card-valor
  font-size 17px
  font-weight 600
  color #111827

.cob-card-pie
  font-size 11px
  color #9ca3af

.cob-card--total
  background #f9fafb

.cob-card--cobrado
  border-left 3px solid #16a34a

.cob-card--liquidado
  border-left 3px solid #f59e0b

.cob-card--pendiente
  border-left 3px solid #6b7280

.cob-card--margen
  background #f9fafb
  border-left 3px solid #1a1f2e

.cob-nota
  margin 12px 0 0
  font-size 12px
  color #4b5563

.cob-nota-link
  border 0
  background none
  padding 0
  color #b45309
  font-weight 600
  cursor pointer
  text-decoration underline

  &.is-activo
    color #1a1f2e

.cob-nota-gris
  color #9ca3af

.cob-filtros
  display flex
  gap 8px
  flex-wrap wrap
  align-items center
  margin-top 16px

.cob-columnas
  position relative

.cob-columnas-menu
  position absolute
  right 0
  top 36px
  z-index 20
  background #fff
  border 1px solid #d1d5db
  border-radius 8px
  padding 8px
  box-shadow 0 6px 18px rgba(0,0,0,.12)
  min-width 190px
  max-height 320px
  overflow auto

.cob-columnas-op
  display flex
  align-items center
  gap 8px
  font-size 13px
  font-weight 400
  padding 3px 4px
  margin 0
  cursor pointer

.cob-tabla-wrap
  margin-top 12px
  overflow-x auto

.cob-tabla
  width 100%
  border-collapse collapse
  font-size 12.5px

  th
    text-align left
    background #f3f4f6
    color #374151
    font-weight 600
    padding 8px 10px
    border-bottom 1px solid #e5e7eb
    white-space nowrap

  td
    padding 8px 10px
    border-bottom 1px solid #f3f4f6
    vertical-align top

  th.num, td.num
    text-align right
    white-space nowrap

  th.marca, td.marca
    width 28px
    text-align center

  tfoot td
    background #f9fafb
    font-weight 600
    border-top 2px solid #e5e7eb

.cob-fila--alerta
  background #fffbeb

.cob-marca
  color #b45309
  font-size 14px

.cob-sub
  display block
  font-size 11px
  color #9ca3af

.cob-vacio
  color #9ca3af

.cob-falta
  color #dc2626
  font-weight 600

.cob-negativo
  color #dc2626

.cob-empty
  text-align center
  color #9ca3af
  padding 24px

.cob-subtotales
  margin-top 20px

  h3
    font-size 13px
    font-weight 600
    margin 0

.cob-subtotales-nota
  font-size 12px
  color #6b7280
  margin 2px 0 8px

.cob-paginador
  display flex
  align-items center
  justify-content center
  gap 12px
  margin-top 16px
  font-size 13px
  color #4b5563
</style>
